import { Component, OnInit, ViewChild } from "@angular/core";
import OverlayScrollbars from "overlayscrollbars";
import { NavigationService } from "src/app/navigation/navigation.service";
import { AngularAssignToolComponent } from "src/app/reusables/assign-tool/angular-assign-tool/angular-assign-tool.component";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { IframeAvailability } from "src/shared/models/broadcasts.model";
import tinymce, { Editor } from "tinymce";
import { AnnouncementsService } from "../announcements.service";

@Component({
    selector: "cv-announcements-new",
    templateUrl: "./announcements-new.component.html",
    styleUrls: ["./announcements-new.component.scss"],
})
export class AnnouncementsNewComponent implements OnInit {
    @ViewChild(AngularAssignToolComponent)
    angularAssignTool: AngularAssignToolComponent;

    public addMode: boolean = false;
    public editMode: boolean = false;
    public reuseMode: boolean = false;
    public selectOpen: boolean = false;
    public showAssign: boolean = false;
    public saving: boolean = false;
    public loadingRecipients: boolean = false;
    public hyperlinkPopupOpen: boolean = false;
    public editConfirmationOpen: boolean = false;
    public validatingIframe: boolean = false;
    public iframeValidated: boolean = false;
    public skipNextIframeValidation: boolean = false;
    public previewFailed: boolean = false;

    public hyperlinkTextTemp: string = "";
    public hyperlinkAddressTemp: string = "";
    public iframeAvailability: IframeAvailability;

    public tinymceEditor: any;
    public placeholderLocalized: string;
    public hyperlinkTooltip: string;

    public groups = [];

    public errorMessages = {
        recipients: null,
        name: null,
        body: null,
        bodyMaxLength: null,
        folder: null,
        hyperlinkText: null,
        hyperlinkAddress: null,
        showDate: null,
        eventDate: null,
        expirationDate: null,
        remindDate: null,
    };

    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
            visibility: "auto",
        },
    };

    constructor(
        private cvLocalizationService: CVLocalizationService,
        public service: AnnouncementsService,
        private navigationService: NavigationService
    ) {}

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Broadcasts");

        this.hyperlinkTooltip = this.cvLocalizationService.getCachedText("Broadcasts", "Preview_Warning");
        this.tinymceEditor = tinymce.activeEditor;

        this.service.currentModes.subscribe((modes) => {
            this.addMode = modes.addMode;
            this.editMode = modes.editMode;
            this.reuseMode = modes.reuseMode;
            this.setGroups();
        });

        this.placeholderLocalized = this.cvLocalizationService.getCachedText(
            "Broadcasts",
            "Select_Date",
            "Select Date"
        );
    }

    async reset() {
        const response = await this.service.getAnnouncements();

        if (response) {
            this.service.progressPopupOpen = false;

            this.service.announcementsList = response.broadcasts;
            this.service.folders = response.folders;

            for(let i=0; i < this.service.announcementsList.length; i++){
                const hyperlinkPromises = []
                if(this.service.announcementsList[i]?.Hyperlinks.length > 0){
                    for(let j =0 ; j < this.service.announcementsList[i].Hyperlinks.length; j++){
                        hyperlinkPromises.push(this.service.GetIFrameAvailability(this.service.announcementsList[i].Hyperlinks[j].HyperlinkAddress));
                    }

                    const hyperlinkResponses = await Promise.all(hyperlinkPromises);
                    hyperlinkResponses.forEach((response, index) => {
                        this.service.announcementsList[i].Hyperlinks[index].ContentSecurityPolicyViolation = response.contentSecurityPolicyViolation;
                        this.service.announcementsList[i].Hyperlinks[index].IframeAvailability = response.responseCode;
                    })
                }
            }

            this.cancel();
        } else {
            this.service.progressPopupOpen = false;
        }
    }

    cancel() {
        this.service.save = {
            BroadcastType: 1,
            BroadcastID: null,
            Title: "",
            BroadcastText: "",
            AnnouncementType: 0,
            Folder: "",
            Urgent: false,
            ShowDate: new Date(),
            EventDate: null,
            ExpirationDate: null,
            RemindDate: null,
            Draft: false,
            Recipients: [],
            RecipientAssignments: [],
            Attachments: [],
            Images: [],
            ImagesData: [],
            Hyperlinks: [],
            MinorEdit: false,
        };
        tinymce.activeEditor.execCommand("mceSetContent", false, "");
        this.errorMessages = {
            recipients: null,
            name: null,
            body: null,
            bodyMaxLength: null,
            folder: null,
            hyperlinkText: null,
            hyperlinkAddress: null,
            showDate: null,
            eventDate: null,
            expirationDate: null,
            remindDate: null,
        };
        this.saving = false;
        this.selectOpen = false;
        this.hyperlinkTextTemp = "";
        this.hyperlinkAddressTemp = "";
        this.iframeAvailability = 0;
        this.iframeValidated = false;
        this.validatingIframe = false;
        this.previewFailed = false;
        this.changeMode(false, false, false);
    }

    removeRecipient(recipient) {
        const index = this.service.save.RecipientAssignments.findIndex((r) =>
            angular.equals(r, recipient)
        );

        if (index >= 0) this.service.save.RecipientAssignments.splice(index, 1);
        this.setGroups();
        const assignObj = recipient.group;
        this.angularAssignTool.removeEvent(assignObj); // Have to manually trigger the remove event in order to differentiate between retained groups and the deletion
        this.validateAll();
    }

    async saveNew(saveAsDraft: boolean) {
        if (this.saving) return;
        this.service.save.BroadcastText = tinymce.activeEditor.getContent();
        if (this.validateAll()) {
            this.saving = true;
            this.service.progressPopupOpen = true;
            this.service.save.Draft = saveAsDraft;
            const response = await this.service.AddNewAnnouncement();
            this.saving = false;
            if (response) this.reset();
        }
    }

    async saveEdit() {
        if (this.saving) return;
        this.service.save.BroadcastText = tinymce.activeEditor.getContent();
        if (this.validateAll()) {
            this.saving = true;
            this.editConfirmationOpen = false;
            this.service.progressPopupOpen = true;
            const response = await this.service.EditAnnouncement();
            this.saving = false;
            if (response) this.reset();
        }
    }

    openEditConfirmation(draft: boolean) {
        if (this.validateAll())
        { 
            this.editConfirmationOpen = true;
            this.service.save.Draft = draft;
            this.service.save.MinorEdit = Date.now() < this.service.save.ShowDate.getTime() ? true : false;
        }
    }

    validateAll() {
        this.validateRecipients();
        this.validateName();
        this.validateBody();
        this.validateBodyMaxLength();
        this.validateFolder();
        this.validateDates();
        this.validateEventDate();
        return this.noErrorMessages();
    }

    validateRecipients() {
        if (this.service.save.RecipientAssignments.length <= 0)
            this.errorMessages.recipients =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Recipients_Required"
                );
        else this.errorMessages.recipients = null;
    }

    validateName() {
        if (this.service.save.Title.length <= 0)
            this.errorMessages.name = this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Name_Required"
            );
        else if (this.service.save.Title.length > 36)
            this.errorMessages.name = this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Name_Characters"
            );
        else this.errorMessages.name = null;
    }

    validateBody() {
        this.errorMessages.body =
            this.service.save.BroadcastText.length <= 0
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Body_Required"
                )
                : null;
    }

    validateBodyMaxLength(){
        const textBody = tinymce.activeEditor.getContent({format: "text"});
        this.errorMessages.bodyMaxLength =
            textBody.length > 500
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Broadcast_Text_Max_Length",
                    "Maximum of 500 characters for the message."
                )
                : null;
    }

    validateFolder() {
        if (this.service.save.Folder.length <= 0)
            this.errorMessages.folder =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Folder_Required"
                );
        else if (this.service.save.Folder.length > 28)
            this.errorMessages.folder =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Folder_Characters"
                );
        else this.errorMessages.folder = null;
    }

    validateShowDate() {
        if (
            this.service.save.ExpirationDate &&
            new Date(this.service.save.ShowDate) >=
                new Date(this.service.save.ExpirationDate)
        )
            this.errorMessages.showDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Show_Date_Before_Expiration"
            );
        else this.errorMessages.showDate = null;

        if(this.service.save.ExpirationDate)
            this.validateExpirationDate();
        if(this.service.save.RemindDate)
            this.validateRemindDate();
    }

    validateEventDate() {
        if (this.service.save.AnnouncementType != 2) {
            this.errorMessages.eventDate = null;
            return;
        }
        // Adding one day to the event date allows for today to be selected
        const eventDateAllowSameDay = new Date(this.service.save.EventDate);
        const eventDateAllowSameDayNext = eventDateAllowSameDay.setDate(
            eventDateAllowSameDay.getDate() + 1
        );
        if (eventDateAllowSameDayNext < Date.now())
            this.errorMessages.eventDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Event_Date_Past_Date"
            );
        else this.errorMessages.eventDate = null;
    }

    validateRemindDate() {
        if (this.service.save.RemindDate == null) {
            this.errorMessages.remindDate = null;
            return;
        }
        if (new Date(this.service.save.RemindDate).getTime() < Date.now())
            this.errorMessages.remindDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Remind_Date_Past_Date"
            );
        else if (
            this.service.save.ShowDate &&
            new Date(this.service.save.RemindDate) <=
                new Date(this.service.save.ShowDate)
        )
            this.errorMessages.remindDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Remind_Date_After"
            );
        else if (
            this.service.save.ExpirationDate &&
            new Date(this.service.save.RemindDate) >=
                new Date(this.service.save.ExpirationDate)
        )
            this.errorMessages.remindDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Remind_Date_Before"
            );
        else this.errorMessages.remindDate = null;
    }

    validateExpirationDate() {
        if (this.service.save.ExpirationDate == null)
        {   
            this.errorMessages.expirationDate = null;
            return;
        }

        if (new Date(this.service.save.ExpirationDate).getTime() < Date.now())
            this.errorMessages.expirationDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Invalid_Expiration_Date"
            );
        else if (
            this.service.save.ShowDate &&
            new Date(this.service.save.ExpirationDate) <=
                new Date(this.service.save.ShowDate)
        )
            this.errorMessages.expirationDate =
            this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Expiration_Date_After"
            );
        else this.errorMessages.expirationDate = null;
    }

    validateDates() {
        this.validateShowDate();
        this.validateRemindDate();
        this.validateExpirationDate();
    }

    validateHyperlinkText() {
        this.errorMessages.hyperlinkText =
            this.hyperlinkTextTemp.length <= 0
                ? this.cvLocalizationService.getCachedText(
                      "Broadcasts",
                      "Hyperlink_Text_Required"
                  )
                : null;
    }

    validateHyperlinkAddress() {
        if (this.hyperlinkAddressTemp.length <= 0)
            this.errorMessages.hyperlinkAddress =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Hyperlink_Address_Required"
                );
        else {
            this.errorMessages.hyperlinkAddress = null;
        }
    }

    async validateIframe() {
        if(this.validatingIframe || this.skipNextIframeValidation){
            this.skipNextIframeValidation = false;
            return;
        }
        this.iframeValidated = false;
        if(this.hyperlinkAddressTemp.length <= 0){
            this.validatingIframe = false;
            this.previewFailed= false;
            return;
        }
        this.validatingIframe = true;

        let response = null;
        response = await this.service.GetIFrameAvailability(this.hyperlinkAddressTemp);
        if(response){
            this.previewFailed = response.contentSecurityPolicyViolation || response.responseCode == 3;
            if(response.outputAddress != this.hyperlinkAddressTemp){
                this.hyperlinkAddressTemp = response.outputAddress;
                this.skipNextIframeValidation = true;
            }
        }
        this.iframeAvailability = this.hyperlinkAddressTemp.startsWith("http:") && response.responseCode == 0 ? 4 : 
                                response.responseCode == 3 ? 0 : response?.responseCode;   // The backend considers 3 an error, but Rejected means we will have no preview which is not an error with the link
        
        this.validatingIframe = false;
        this.iframeValidated = true;
    }

    pastePreprocess(plugin: Editor, args){
        const splitContent = args.content.split('<br />');
        const cleanedContent = [];
        splitContent.forEach((line) => {
            cleanedContent.push("<div>" + line + "</div>");
        });
        args.content = cleanedContent.join('');
    }

    noErrorMessages() {
        const errorMessages = Object.values(this.errorMessages);
        for (let i = 0; i < errorMessages.length; i++) {
            if (errorMessages[i] != null) return false;
        }
        return true;
    }

    assignToolToggle(b: boolean) {
        if (!b) {
            // when assign tool closes, validate Recipients
            this.setGroups();
            this.validateRecipients();
        }
    }

    addHyperlink() {
        // Ensure that URL is valid
        this.validateHyperlinkText();
        this.validateHyperlinkAddress();
        if (
            this.errorMessages.hyperlinkText == null &&
            this.errorMessages.hyperlinkAddress == null
        ) {
            // Add hyperlink to list, close hyperlink entry popup
            this.service.save.Hyperlinks.push({
                HyperlinkAddress: this.hyperlinkAddressTemp,
                HyperlinkText: this.hyperlinkTextTemp,
                ContentSecurityPolicyViolation: this.previewFailed,
                IframeAvailability: this.iframeAvailability,
            });
            this.closeHyperlinkPopup();
        }
    }

    closeHyperlinkPopup() {
        this.hyperlinkTextTemp = "";
        this.hyperlinkAddressTemp = "";
        this.hyperlinkPopupOpen = false;
        this.validatingIframe = false;
        this.iframeAvailability = 0;
        this.iframeValidated = false;
        this.previewFailed = false;
    }

    attachmentPicked(event, isImage: boolean = false) {
        const files = event.target;
        let fileNameArray = files.value.split("\\");
        const fileName = fileNameArray[fileNameArray.length - 1];
        fileNameArray = fileName.split(".");

        let attachmentName = "";
        for (let i = 0; i < fileNameArray.length - 1; i++) {
            attachmentName += fileNameArray[i] + ".";
        }
        attachmentName = attachmentName.slice(0, -1);

        let extension = fileNameArray[fileNameArray.length - 1];
        if (extension == "html" && isImage) return;
        const wholeFile = files.files[0];
        let fileContents = null;
        const fileByteArray = [];
        let fileString = "";

        const reader = new FileReader();
        reader.onload = (evt) => {
            fileContents = evt.target.result;
            let array = new Uint8Array(fileContents);
            for (let i = 0; i < array.length; i++) {
                fileByteArray.push(array[i]);
                fileString += String.fromCharCode(array[i]);
            }

            let fileStringConverted = window.btoa(fileString);
            if (isImage) {
                this.service.save.Images.push({
                    AltText: attachmentName,
                    FileExtension: extension,
                    FileString: fileStringConverted,
                });
                this.service.save.ImagesData.push(fileByteArray);
            } else {
                this.service.save.Attachments.push({
                    AttachmentName: attachmentName,
                    AttachmentExtension: extension,
                    FileData: fileByteArray,
                });
            }
        };
        reader.readAsArrayBuffer(wholeFile);
    }

    removeAttachment(type, index) {
        switch (type) {
            case 1:
                this.service.save.Attachments.splice(index, 1);
                break;
            case 2:
                this.service.save.Images.splice(index, 1);
                this.service.save.ImagesData.splice(index, 1);
                break;
            case 3:
                this.service.save.Hyperlinks.splice(index, 1);
                break;
        }
    }

    handleTypeChange(type: number){
        this.service.save.AnnouncementType = type;
        if (type == 2) {
            this.service.save.EventDate = new Date();
        } else {
            this.service.save.EventDate = null;
        }
    }

    setGroups() {
        const groups = [];
        this.service.save.RecipientAssignments.forEach((recipient) => {
            groups.push({ ...recipient.group });
        });
        this.groups = groups;
    }

    noRecipientsSet() {
        return angular.equals(this.service.save.RecipientAssignments, []);
    }

    changeMode(
        addMode: boolean = false,
        editMode: boolean = false,
        reuseMode: boolean = false
    ) {
        this.addMode = addMode;
        this.editMode = editMode;
        this.reuseMode = reuseMode;
        this.service.changeMode(addMode, editMode, reuseMode);
        this.setGroups();
    }

    tinyMceLanguage() {
        const loggedInLang = this.navigationService.loggedInLang;
        switch (loggedInLang) {
            case "Français":
            case "Français Canadien":
                return "fr";
            case "Español":
                return "es";
            case "Deutsch":
                return "de";
            case "Portugues do Brasil":
                return "pt_BR";
            case "Japanese":
                return "ja";
            case "Dutch":
                return "nl";
            default:
                return undefined;
        }
    }
}
